import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Bell, BellOff, Pencil, Trash2, Plus, CheckCheck, Clock, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAlerts } from "../../context/AlertsContext";
import { ALERT_CATALOG } from "../../engine";
import { fmtPrice, meta } from "../../lib/symbols";
import AlertForm from "./AlertForm";

const TABS = [{ id: "active", label: "Alerts" }, { id: "log", label: "Triggered" }];
const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString([], { hour12: false }) : "—");
const describe = (a) => {
  const t = ALERT_CATALOG.find((x) => x.type === a.kind);
  const cond = t?.conditions.find((c) => c.id === a.operator)?.label ?? a.operator;
  const val = a.params?.value != null ? ` ${fmtPrice(+a.params.value, a.kind === "price" ? meta(a.symbol).decimals : 2)}` : "";
  return `${t?.label ?? a.kind} · ${cond}${val}`;
};

export default function AlertManager() {
  const { managerOpen, setManagerOpen, alerts, events, unread, draft, updateAlert, deleteAlert, markRead } = useAlerts();
  const [tab, setTab] = useState("active");
  const [mode, setMode] = useState("list"); // list | create | edit
  const [editing, setEditing] = useState(null);

  useEffect(() => { if (managerOpen) { setMode(draft ? "create" : "list"); setTab("active"); } }, [managerOpen, draft]);

  const toggle = async (a) => {
    try { await updateAlert(a.id, { active: !a.active }); toast.success(a.active ? "Alert paused" : "Alert armed"); }
    catch { toast.error("Could not update alert"); }
  };
  const remove = async (a) => { try { await deleteAlert(a.id); toast.success("Alert deleted"); } catch { toast.error("Could not delete"); } };

  return (
    <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
      <DialogContent className="max-w-2xl bg-panel border-border p-0 overflow-hidden" data-testid="alert-manager">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-heading flex items-center gap-2"><Bell className="h-4 w-4 text-primary" /> Alert Manager</DialogTitle>
          <DialogDescription className="text-xs">Evaluated server-side by the Fela R.I.O Alert Engine — keeps running when the chart is closed.</DialogDescription>
        </DialogHeader>

        {mode !== "list" ? (
          <div className="p-5">
            <AlertForm initial={mode === "edit" ? editing : draft} editing={mode === "edit" ? editing : null} onDone={() => { setMode("list"); setEditing(null); }} />
            <button data-testid="alert-form-cancel" onClick={() => { setMode("list"); setEditing(null); }} className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground py-1">Cancel</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1 px-3 pt-3">
              {TABS.map((t) => (
                <button key={t.id} data-testid={`alerts-tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                  {t.label}{t.id === "log" && unread > 0 ? ` (${unread})` : t.id === "active" ? ` (${alerts.length})` : ""}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-1">
                {tab === "log" && events.some((e) => !e.read) && (
                  <button data-testid="alerts-mark-read" onClick={() => markRead(null)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-secondary"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>
                )}
                <button data-testid="alerts-new-btn" onClick={() => setMode("create")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold"><Plus className="h-3.5 w-3.5" /> New alert</button>
              </div>
            </div>

            <div className="p-3 max-h-[60vh] overflow-y-auto scrollbar-thin space-y-1.5">
              {tab === "active" && (alerts.length === 0
                ? <Empty text="No alerts yet. Arm one for V50 1s — BOS, price, RSI or the Fela R.I.O tick engine." />
                : alerts.map((a) => (
                  <div key={a.id} data-testid={`alert-row-${a.id}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${a.active ? "border-border bg-background" : "border-border/50 opacity-60"}`}>
                    <button data-testid={`alert-toggle-${a.id}`} onClick={() => toggle(a)} title={a.active ? "Pause" : "Arm"}
                      className={`h-8 w-8 rounded-lg flex items-center justify-center ${a.active ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                      {a.active ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        {a.display}<span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-mono-nums">{a.timeframe}</span>
                        <span className="text-[10px] text-muted-foreground">{a.repeat === "once" ? "once" : `every · ${a.cooldown_s}s`}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate" data-testid={`alert-desc-${a.id}`}>{describe(a)}{a.note ? ` — ${a.note}` : ""}</div>
                    </div>
                    <div className="text-right text-[11px] text-muted-foreground hidden sm:block">
                      <div className="flex items-center gap-1 justify-end"><Zap className="h-3 w-3" /> {a.trigger_count}×</div>
                      <div className="flex items-center gap-1 justify-end"><Clock className="h-3 w-3" /> {a.triggered_at ? fmtTs(a.triggered_at) : "never"}</div>
                    </div>
                    <button data-testid={`alert-edit-${a.id}`} onClick={() => { setEditing(a); setMode("edit"); }} className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-secondary"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`alert-delete-${a.id}`} onClick={() => remove(a)} className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-sell"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )))}
              {tab === "log" && (events.length === 0
                ? <Empty text="No triggered alerts yet." />
                : events.map((e) => (
                  <div key={e.id} data-testid={`alert-event-${e.id}`} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border ${e.read ? "border-border/50" : "border-primary/40 bg-primary/5"}`}>
                    <Zap className={`h-4 w-4 mt-0.5 ${e.read ? "text-muted-foreground" : "text-primary"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-2">{e.display}<span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground uppercase">{e.kind}</span><span className="text-[10px] text-muted-foreground font-mono-nums">{e.timeframe}</span></div>
                      <div className="text-xs text-foreground/90">{e.message}</div>
                    </div>
                    <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtTs(e.fired_at)}</div>
                  </div>
                )))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Empty({ text }) {
  return <div className="py-10 text-center text-sm text-muted-foreground" data-testid="alerts-empty">{text}</div>;
}
